/**
 * University Browser with Quick Apply
 * Alternative path to application - browse universities and apply directly
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import SEO from '@/components/SEO';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Search,
  GraduationCap,
  MapPin,
  TrendingUp,
  Users,
  ChevronRight,
  Loader2,
  Building2,
  Globe,
  Plus,
  Check,
  X,
  ShoppingCart,
  Send,
  FileText,
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const UniversityBrowserPage = () => {
  const { isDark } = useTheme();
  const { user, token, loading: authLoading, isAuthenticated } = useStudentAuth();
  const navigate = useNavigate();
  
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [countries, setCountries] = useState([]);
  const [selectedUniversities, setSelectedUniversities] = useState(new Set());
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applying, setApplying] = useState(false);
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/uac-desk/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    fetchUniversities();
    if (user?.user_id) {
      fetchProfile();
    }
  }, [user]);

  const fetchUniversities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/university-pages/public/list?limit=500`);
      if (res.ok) {
        const data = await res.json();
        setUniversities(data.universities || []);
        const uniqueCountries = [...new Set((data.universities || []).map(u => u.country).filter(Boolean))];
        setCountries(uniqueCountries.sort());
      }
    } catch (error) {
      console.error('Error fetching universities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/counselor/profile/${user.user_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        
        // Extract documents
        if (data.documents && typeof data.documents === 'object') {
          const docsArray = Object.entries(data.documents)
            .filter(([key, value]) => value && value.uploaded)
            .map(([key, value]) => ({
              doc_type: key,
              doc_name: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
            }));
          setDocuments(docsArray);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const toggleUniversity = (slug) => {
    setSelectedUniversities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(slug)) {
        newSet.delete(slug);
      } else {
        if (newSet.size >= 5) {
          toast.error('You can select up to 5 universities at a time');
          return prev;
        }
        newSet.add(slug);
      }
      return newSet;
    });
  };

  const getSelectedUniversityDetails = () => {
    return universities.filter(u => selectedUniversities.has(u.slug));
  };

  const hasIdentityDoc = documents.some(d => d.doc_type === 'aadhaar' || d.doc_type === 'passport');
  const hasAcademicDoc = documents.some(d => ['10th_marksheet', '12th_marksheet', 'degree_certificate', 'transcript'].includes(d.doc_type));
  const canApply = hasIdentityDoc && hasAcademicDoc;

  const handleApply = async () => {
    if (!canApply) {
      toast.error('Please upload required documents first');
      return;
    }

    const selectedUnis = getSelectedUniversityDetails();
    if (selectedUnis.length === 0) {
      toast.error('Please select at least one university');
      return;
    }

    setApplying(true);
    try {
      // Create applications for each selected university
      for (const uni of selectedUnis) {
        await fetch(`${API_URL}/api/applications/create/${user.user_id}`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            university_name: uni.university_name,
            university_slug: uni.slug,
            program: 'General Program',
            intake: 'Feb/July 2026',
            country: uni.country,
            notes: 'Applied via University Browser'
          })
        });
      }

      toast.success(`Applied to ${selectedUnis.length} universities! Track your applications in the dashboard.`);
      setShowApplyDialog(false);
      setSelectedUniversities(new Set());
      
      // Navigate to application tracker
      navigate('/uac-desk/applications');
    } catch (error) {
      console.error('Error applying:', error);
      toast.error('Failed to submit applications');
    } finally {
      setApplying(false);
    }
  };

  // Filter universities
  const filteredUniversities = universities.filter(uni => {
    const matchesSearch = !search || 
      uni.university_name?.toLowerCase().includes(search.toLowerCase()) ||
      uni.country?.toLowerCase().includes(search.toLowerCase());
    const matchesCountry = countryFilter === 'all' || 
      uni.country?.toLowerCase() === countryFilter.toLowerCase();
    return matchesSearch && matchesCountry;
  });

  if (authLoading || loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f1a1f]' : 'bg-slate-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen pb-24 ${isDark ? 'bg-[#0f1a1f]' : 'bg-slate-50'}`}>
      {/* SEO Meta Tags */}
      <SEO 
        title="Browse Universities | Find Best Universities Abroad"
        description="Browse 500+ top universities in USA, UK, Canada, Australia & more. Compare rankings, fees, courses. Apply directly through UniXperts. Expert guidance included."
        keywords="universities abroad, best universities for Indian students, university rankings, study abroad universities, USA universities, UK universities, Canada universities, Australia universities, university admission, university fees"
        canonical="https://unixperts.com/universities"
        noindex={true}
      />
      
      {/* Header */}
      <div className={`sticky top-0 z-40 ${isDark ? 'bg-[#1a2830]/95 backdrop-blur-lg border-b border-white/10' : 'bg-white/95 backdrop-blur-lg border-b border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/uac-desk/dashboard">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  Browse Universities
                </h1>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  Select universities and apply directly
                </p>
              </div>
            </div>
            <Link to="/uac-desk/fast-track">
              <Button variant="outline" className="rounded-full">
                <Sparkles className="w-4 h-4 mr-2" /> Use AI Assistant Instead
              </Button>
            </Link>
          </div>
          
          {/* Search & Filters */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
              <Input
                placeholder="Search universities..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-10 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
              />
            </div>
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className={`w-full sm:w-[160px] ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}>
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map(country => (
                  <SelectItem key={country} value={country.toLowerCase()}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Universities Grid */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUniversities.map((uni) => {
            const isSelected = selectedUniversities.has(uni.slug);
            return (
              <Card
                key={uni.slug}
                className={`cursor-pointer transition-all ${
                  isSelected 
                    ? 'ring-2 ring-ux-cyan border-ux-cyan' 
                    : isDark 
                      ? 'bg-white/5 border-white/10 hover:border-white/30' 
                      : 'hover:border-ux-cyan/30'
                }`}
                onClick={() => toggleUniversity(uni.slug)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                      isSelected 
                        ? 'bg-ux-cyan border-ux-cyan' 
                        : isDark ? 'border-white/30' : 'border-slate-300'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className={`font-semibold text-sm leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {uni.university_name}
                        </h3>
                        {uni.world_ranking && (
                          <Badge className="bg-ux-amber/10 text-ux-amber border-0 shrink-0 text-xs">
                            #{uni.world_ranking}
                          </Badge>
                        )}
                      </div>
                      <div className={`flex items-center gap-1 mt-1 text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                        <MapPin className="w-3 h-3" />
                        {uni.city && `${uni.city}, `}{uni.country}
                      </div>
                      {uni.hero_tagline && (
                        <p className={`mt-2 text-xs line-clamp-2 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                          {uni.hero_tagline}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Floating Selection Bar */}
      {selectedUniversities.size > 0 && (
        <div className={`fixed bottom-0 left-0 right-0 z-50 ${isDark ? 'bg-[#1a2830] border-t border-white/10' : 'bg-white border-t border-slate-200 shadow-lg'}`}>
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge className="bg-ux-cyan text-white px-3 py-1">
                {selectedUniversities.size} selected
              </Badge>
              <div className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                {getSelectedUniversityDetails().map(u => u.university_name).join(', ').slice(0, 60)}
                {getSelectedUniversityDetails().map(u => u.university_name).join(', ').length > 60 && '...'}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => setSelectedUniversities(new Set())}
                className="rounded-full"
              >
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
              <Button 
                onClick={() => setShowApplyDialog(true)}
                className="bg-ux-cyan hover:bg-ux-cyan/90 text-white rounded-full"
              >
                <Send className="w-4 h-4 mr-2" /> Apply Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-[#1a2830] border-white/10' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Apply to Universities</DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              You're applying to {selectedUniversities.size} universities
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Selected Universities */}
            <div>
              <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                Selected Universities
              </h4>
              <div className="space-y-2">
                {getSelectedUniversityDetails().map(uni => (
                  <div key={uni.slug} className={`flex items-center gap-2 p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                    <Building2 className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                    <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-700'}`}>
                      {uni.university_name}
                    </span>
                    <Badge className="ml-auto text-xs">{uni.country}</Badge>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Document Status */}
            <div>
              <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                Document Status
              </h4>
              <div className={`p-3 rounded-lg ${canApply ? 'bg-green-500/10 border border-green-500/30' : 'bg-orange-500/10 border border-orange-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {hasIdentityDoc ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                  )}
                  <span className={`text-sm ${hasIdentityDoc ? 'text-green-600' : 'text-orange-600'}`}>
                    Identity Document (Aadhaar/Passport)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {hasAcademicDoc ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                  )}
                  <span className={`text-sm ${hasAcademicDoc ? 'text-green-600' : 'text-orange-600'}`}>
                    Academic Document (Marksheet/Degree)
                  </span>
                </div>
              </div>
              {!canApply && (
                <Link to="/uac-desk/fast-track" className="block mt-2">
                  <Button variant="outline" size="sm" className="w-full rounded-full">
                    <FileText className="w-4 h-4 mr-2" /> Upload Documents
                  </Button>
                </Link>
              )}
            </div>
            
            {/* Info */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                After applying, our counselors will review your profile and contact you within 24-48 hours. 
                You can track your application status in the Applications section.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApply}
              disabled={applying || !canApply}
              className="bg-ux-cyan hover:bg-ux-cyan/90 text-white"
            >
              {applying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Applications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UniversityBrowserPage;
